/**
 * ⚠️ DÉPRÉCIÉ — conservé pour compatibilité offline uniquement
 * La validation principale est dans src/shared/license/ 
 * Voir TICKET-23 (résolu) et le module licence serveur
 *
 * TICKET-23 ⚪ Remplacer par un vrai backend (Netlify Function + HMAC-SHA256)
 * Actuellement : 100% client-side, contournable via localStorage.setItem()
 * Dépend de : TICKET-16 (CI/CD mature)
 * Voir aussi : TICKET-2 (XSS — ne pas exposer de données sensibles ici)
 *
 * IndxOne Hub — License compatibility layer
 *
 * Offline-first compatibility for legacy browser state. The source of truth
 * now comes from the server-issued license flow; this module only mirrors
 * validated entitlement into localStorage for UX continuity.
 */
(function () {
  'use strict';

  const DAY = 24 * 60 * 60 * 1000;
  const STARTER_DURATION_DAYS = 30;
  const DEFAULT_DURATION_DAYS = 365;
  const OWNER_DURATION_DAYS = 3650;

  const fallbackStorage = {
    keys: {
      current: 'indxone.license.v1',
      legacy: 'indxone.license.key'
    },
    read() {
      try { return JSON.parse(localStorage.getItem(this.keys.current) || 'null'); } catch { return null; }
    },
    write(record) {
      localStorage.setItem(this.keys.current, JSON.stringify(record));
      if (record?.key) localStorage.setItem(this.keys.legacy, record.key);
    },
    clear() {
      localStorage.removeItem(this.keys.current);
      localStorage.removeItem(this.keys.legacy);
    },
    readLegacyKey() {
      return localStorage.getItem(this.keys.legacy);
    }
  };

  const store = window.IndxLicenseStorage || fallbackStorage;

  // Salt stored as codepoints to avoid trivial string grep.
  const _S = [73, 78, 68, 88, 79, 78, 69, 50, 48, 50, 54];

  function _salt() {
    return _S.map(c => String.fromCharCode(c)).join('');
  }

  function _hash(body) {
    let h = 0x811c9dc5 >>> 0;
    const data = body + _salt();
    for (let i = 0; i < data.length; i++) {
      h ^= data.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return (h % 1679616).toString(36).toUpperCase().padStart(4, '0');
  }

  function _strip(key) {
    return (key || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  function _normalizeStoredKey(key) {
    const raw = (key || '').trim();
    if (raw.toUpperCase().startsWith('INDX1.')) return raw;
    const compact = _strip(raw);
    const ownerMatch = /^INDXOWN1([A-Z0-9]{4})([A-Z0-9]{4})$/.exec(compact);
    if (ownerMatch) return `INDX-OWN1-${ownerMatch[1]}-${ownerMatch[2]}`;
    return compact;
  }

  function _iso(ts) {
    return new Date(ts).toISOString();
  }

  function _formatDate(iso) {
    if (!iso) return null;
    try {
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(new Date(iso));
    } catch {
      return iso.slice(0, 10);
    }
  }

  function _classify(key) {
    const k = _strip(key);
    const starterMatch = /^INDXST1([A-Z0-9]{4})([A-Z0-9]{4})$/.exec(k);
    if (starterMatch) {
      const starterBody = `ST1${starterMatch[1]}`;
      return _hash(starterBody) === starterMatch[2]
        ? { kind: 'starter', tier: 'starter', plan: 'starter', key: k, body: starterBody, durationDays: STARTER_DURATION_DAYS }
        : null;
    }

    const ownerMatch = /^INDXOWN1([A-Z0-9]{4})([A-Z0-9]{4})$/.exec(k);
    if (ownerMatch) {
      const ownerBody = `OWN1${ownerMatch[1]}`;
      return _hash(ownerBody) === ownerMatch[2]
        ? { kind: 'owner', tier: 'pro', plan: 'pro', key: k, body: ownerBody, durationDays: OWNER_DURATION_DAYS }
        : null;
    }

    if (k.startsWith('INDX') && k.length === 16) {
      const body = k.slice(4, 12);
      const check = k.slice(12);
      return _hash(body) === check
        ? { kind: 'pro', tier: 'pro', plan: 'pro', key: k, body, durationDays: DEFAULT_DURATION_DAYS }
        : null;
    }

    return null;
  }

  function _normalizeRecord(record) {
    if (!record || typeof record !== 'object') return null;
    if (!record.key || !record.tier) return null;
    return {
      version: 1,
      tier: record.tier,
      plan: record.plan || record.tier,
      kind: record.kind || record.tier || 'pro',
      key: _normalizeStoredKey(record.key),
      body: record.body || null,
      activatedAt: record.activatedAt || _iso(Date.now()),
      expiresAt: record.expiresAt || null,
      source: record.source || 'manual'
    };
  }

  function _stateFromRecord(record) {
    const normalized = _normalizeRecord(record);
    if (!normalized) {
      return {
        tier: 'free',
        status: 'free',
        isPro: false,
        isStarter: false,
        isExpired: false,
        expiresAt: null,
        expiresLabel: null,
        plan: null,
        key: null,
        body: null,
        source: null
      };
    }

    const expiresAtMs = normalized.expiresAt ? Date.parse(normalized.expiresAt) : null;
    const isExpired = Number.isFinite(expiresAtMs) && expiresAtMs <= Date.now();
    const isPro = normalized.tier === 'pro' && !isExpired;
    const isStarter = normalized.tier === 'starter' && !isExpired;
    const activeTier = isPro ? 'pro' : isStarter ? 'starter' : 'free';

    return {
      ...normalized,
      tier: activeTier,
      status: isPro ? 'pro' : isStarter ? 'starter' : 'expired',
      isPro,
      isStarter,
      isExpired,
      expiresLabel: _formatDate(normalized.expiresAt)
    };
  }

  function _readRecord() {
    const current = store.read();
    if (current) return current;

    const legacy = store.readLegacyKey();
    const classified = _classify(legacy);
    if (!classified) return null;

    const now = Date.now();
    const migrated = {
      version: 1,
      tier: 'pro',
      plan: 'pro',
      kind: classified.kind,
      key: classified.key,
      body: classified.body,
      activatedAt: _iso(now),
      expiresAt: _iso(now + classified.durationDays * DAY),
      source: 'legacy'
    };
    store.write(migrated);
    return migrated;
  }

  function _emitChange() {
    window.dispatchEvent(new CustomEvent('indxlicense:changed', {
      detail: window.IndxLicense.getState()
    }));
  }

    window.IndxLicense = {
    classify: _classify,

    getState() {
      return _stateFromRecord(_readRecord());
    },

    isPro() {
      return this.getState().isPro === true;
    },

    isOwner() {
      return this.getState().kind === 'owner' && this.isPro();
    },

    isStarter() {
      const state = this.getState();
      return state.isStarter === true;
    },

    activate(key, options = {}) {
      const requestedTier = options.tier || options.plan || null;
      const classified = _classify(key);
      const tier = requestedTier || classified?.tier || null;
      if (!tier) {
        return {
          ok: false,
          error: 'invalid',
          message: 'Clé invalide. Vérifie le format.'
        };
      }

      const now = Date.now();
      const expiresAt = options.expiresAt
        ? new Date(options.expiresAt).toISOString()
        : _iso(now + (tier === 'starter'
          ? STARTER_DURATION_DAYS
          : classified?.durationDays || DEFAULT_DURATION_DAYS) * DAY);

      const body = classified?.body || `${tier.toUpperCase()}-${now.toString(36).toUpperCase()}`;
      const keyValue = classified?.key || key || options.key || `${tier.toUpperCase()}-LOCAL`;

      const record = {
        version: 1,
        tier,
        plan: tier,
        kind: options.kind || classified?.kind || tier,
        key: _normalizeStoredKey(keyValue),
        body,
        activatedAt: _iso(now),
        expiresAt,
        source: options.source || 'manual'
      };

      store.write(record);
      _emitChange();

      return {
        ok: true,
        state: this.getState(),
        message: tier === 'starter'
          ? 'Licence Starter activée sur ce navigateur.'
          : 'Licence Pro activée sur ce navigateur.'
      };
    },

    deactivate() {
      store.clear();
      _emitChange();
    },

    getBody() {
      return this.getState().body || null;
    },

    getKey() {
      const key = this.getState().key;
      return key ? this.format(key) : null;
    },

    getExpiresAt() {
      return this.getState().expiresAt || null;
    },

    format(raw) {
      const k = _strip(raw);
      if (/^INDXOWN1[A-Z0-9]{8}$/.test(k)) {
        return `${k.slice(0, 4)}-${k.slice(4, 8)}-${k.slice(8, 12)}-${k.slice(12)}`;
      }
      if (k.length !== 16) return raw;
      return `${k.slice(0, 4)}-${k.slice(4, 8)}-${k.slice(8, 12)}-${k.slice(12)}`;
    }
  };
})();
